import React, { useState } from 'react';
import debounce from 'lodash/debounce';
import { Controller } from 'react-hook-form';
import _get from 'lodash/get';
import Select from '../baseui/input/select/Select';

interface PaginatedSelectProps {
	value: any | any[];
	onChange: (value: any | any[]) => void;
	onChangeCallback?: (value: any | any[]) => void;
	fetchOptions: (search?: string) => Promise<Array<{ id: string | number; label: string }>>;
	placeholder?: string;
	error?: string;
	disabled?: boolean;
	labelKey?: string;
	valueKey?: string;
	multi?: boolean;
	debounceTime?: number;
}

const SearchSelect: React.FC<PaginatedSelectProps> = ({
	value,
	onChange,
	onChangeCallback,
	fetchOptions,
	placeholder = 'Select...',
	error,
	disabled,
	labelKey = 'label',
	valueKey = 'id',
	multi = false,
	debounceTime = 300
}) => {
	const [options, setOptions] = useState<Array<any>>([]);
	const [isLoading, setIsLoading] = useState(false);

	const loadOptions = async (searchTerm?: string) => {
		setIsLoading(true);
		try {
			const response = await fetchOptions(searchTerm);
			setOptions(response);
		} catch (err) {
			console.error('Error loading options:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const debouncedSearch = debounce((term: string) => {
		loadOptions(term);
	}, debounceTime);

	const handleChange = (changedValue: any) => {
		onChange(changedValue);
		if (onChangeCallback) {
			onChangeCallback(changedValue);
		}
	};

	const handleInputChange = (term: string) => {
		debouncedSearch(term);
	};

	const handleOpen = () => {
		if (options.length === 0) {
			loadOptions();
		}
	};

	return (
		<Select
			options={options}
			value={value}
			onChange={handleChange}
			onInputChange={(e) => handleInputChange(e?.target?.value)}
			onOpen={handleOpen}
			isLoading={isLoading}
			placeholder={placeholder}
			error={Boolean(error)}
			disabled={disabled}
			searchable
			maxDropdownHeight='300px'
			labelKey={labelKey}
			valueKey={valueKey}
			multi={multi}
			onChangeCallback={onChangeCallback}
		/>
	);
};

interface ISearchSelectInput {
	name: string;
	control: any;
	defaultValue: any | any[];
	onChangeCallback?: (value: any | any[]) => void;
	fetchOptions: (search?: string) => Promise<Array<{ id: string | number; label: string }>>;
	placeholder?: string;
	disabled?: boolean;
	labelKey?: string;
	valueKey?: string;
	multi?: boolean;
}

const SearchSelectInput: React.FC<ISearchSelectInput> = (props) => {
	const { name, control, defaultValue, onChangeCallback } = props;

	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { onChange, value }, fieldState: { error } }) => {
				return (
					<SearchSelect
						{...props}
						value={value}
						onChange={onChange}
						onChangeCallback={onChangeCallback}
						error={error?.message}
					/>
				);
			}}
			defaultValue={defaultValue && _get(defaultValue, ['field', ...defaultValue.path])}
		/>
	);
};

export default SearchSelectInput;
